import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  closestCorners,
  KeyboardSensor,
  TouchSensor,
  MouseSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { snapCenterToCursor, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { usePhotoPreview } from '@/components/PhotoPreview';

// Sortable player item
function SortablePlayerItem({ player, index, listType }) {
  const previewPhoto = usePhotoPreview();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: player.user_id,
  });

  // Apply transform consistently for proper positioning calculations
  // Fade out the original item when dragging (DragOverlay shows the dragged copy)
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-3 rounded-lg border ${
        listType === 'registered'
          ? 'bg-green-50 border-green-200'
          : 'bg-yellow-50 border-yellow-200'
      } transition-all`}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <span className="text-gray-500 font-mono text-sm flex-shrink-0">#{index + 1}</span>
        <div className="flex items-center gap-2 min-w-0">
          {player.profile_photo_url && (
            <img
              src={player.profile_photo_url}
              alt={player.name}
              loading="lazy"
              onClick={(e) => { e.stopPropagation(); previewPhoto(player.profile_photo_url, player.name); }}
              onPointerDown={(e) => e.stopPropagation()}
              className="w-8 h-8 rounded-full object-cover flex-shrink-0 cursor-zoom-in"
            />
          )}
          <span className="font-medium text-gray-800 truncate">{player.name}</span>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {player.paid && (
          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
            Paid
          </span>
        )}
        {player.is_first_game && (
          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
            1st
          </span>
        )}
        {/* ONLY the handle is draggable */}
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-3 hover:bg-gray-200 rounded-lg transition-colors touch-none"
          style={{ touchAction: 'none' }}
          type="button"
        >
          <svg
            className="w-6 h-6 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default function DraggablePlayerList({
  registeredPlayers,
  waitingList,
  onReorder,
  playerLimit,
}) {
  const [activeId, setActiveId] = useState(null);
  const [localRegistered, setLocalRegistered] = useState(registeredPlayers);
  const [localWaiting, setLocalWaiting] = useState(waitingList);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Ref for the component wrapper
  const wrapperRef = useRef(null);
  // Ref for the actual scroll container (parent DialogContent)
  const scrollContainerRef = useRef(null);

  // Find the parent scroll container on mount
  useEffect(() => {
    if (wrapperRef.current) {
      // Find the nearest scrollable ancestor (DialogContent with overflow-y-auto)
      let parent = wrapperRef.current.parentElement;
      while (parent) {
        const overflowY = window.getComputedStyle(parent).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') {
          scrollContainerRef.current = parent;
          break;
        }
        parent = parent.parentElement;
      }
    }
  }, []);

  // Better sensor configuration for mobile devices
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      // Require 300ms press before drag starts on mobile
      activationConstraint: {
        delay: 300,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) {
      setActiveId(null);
      return;
    }

    // Find which list the dragged item is in
    const activeInRegistered = localRegistered.find((p) => p.user_id === active.id);
    const activeInWaiting = localWaiting.find((p) => p.user_id === active.id);

    // Find which list the target is in
    const overInRegistered = localRegistered.find((p) => p.user_id === over.id);
    const overInWaiting = localWaiting.find((p) => p.user_id === over.id);

    // Same list reordering
    if (activeInRegistered && overInRegistered) {
      // Reorder within registered list
      const oldIndex = localRegistered.findIndex((p) => p.user_id === active.id);
      const newIndex = localRegistered.findIndex((p) => p.user_id === over.id);
      setLocalRegistered(arrayMove(localRegistered, oldIndex, newIndex));
      setHasChanges(true);
    } else if (activeInWaiting && overInWaiting) {
      // Reorder within waiting list
      const oldIndex = localWaiting.findIndex((p) => p.user_id === active.id);
      const newIndex = localWaiting.findIndex((p) => p.user_id === over.id);
      setLocalWaiting(arrayMove(localWaiting, oldIndex, newIndex));
      setHasChanges(true);
    }
    // Moving between lists
    else if (activeInRegistered && overInWaiting) {
      // Move from registered to waiting
      const playerToMove = localRegistered.find((p) => p.user_id === active.id);
      const targetIndex = localWaiting.findIndex((p) => p.user_id === over.id);
      let newRegistered = localRegistered.filter((p) => p.user_id !== active.id);
      let newWaiting = [...localWaiting];
      
      // Insert player at target position in waiting list
      newWaiting.splice(targetIndex, 0, playerToMove);
      
      // If main list is not full and there are players in waiting, promote first from waiting
      if (newRegistered.length < playerLimit && newWaiting.length > 1) {
        // Find the first player in waiting list (excluding the one we just moved there)
        const firstWaiting = newWaiting.find(p => p.user_id !== active.id);
        if (firstWaiting) {
          newWaiting = newWaiting.filter(p => p.user_id !== firstWaiting.user_id);
          newRegistered.push(firstWaiting);
        }
      }
      
      setLocalRegistered(newRegistered);
      setLocalWaiting(newWaiting);
      setHasChanges(true);
    } else if (activeInWaiting && overInRegistered) {
      // Move from waiting to registered
      const playerToMove = localWaiting.find((p) => p.user_id === active.id);
      const targetIndex = localRegistered.findIndex((p) => p.user_id === over.id);
      let newRegistered = [...localRegistered];
      let newWaiting = localWaiting.filter((p) => p.user_id !== active.id);
      
      if (localRegistered.length >= playerLimit) {
        // Main list is full - automatically swap last player to waiting list
        const lastPlayer = newRegistered[newRegistered.length - 1];
        newRegistered.pop(); // Remove last player from main list
        newWaiting.unshift(lastPlayer); // Add to front of waiting list
      }
      
      // Insert player at target position in registered list
      newRegistered.splice(targetIndex, 0, playerToMove);
      setLocalWaiting(newWaiting);
      setLocalRegistered(newRegistered);
      setHasChanges(true);
    }

    setActiveId(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const registeredIds = localRegistered.map(p => p.user_id);
      const waitingIds = localWaiting.map(p => p.user_id);
      
      console.log('=== SAVING PLAYER ORDER ===');
      console.log('Registered player IDs:', registeredIds);
      console.log('Waiting list IDs:', waitingIds);
      console.log('Total players:', registeredIds.length + waitingIds.length);
      
      await onReorder(localRegistered, localWaiting);
      
      setHasChanges(false);
      console.log('✅ Save successful!');
    } catch (error) {
      console.error('❌ SAVE ERROR:', error);
      console.error('Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });
      
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to save changes. Please try again.';
      alert('Error saving changes:\n' + errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setLocalRegistered(registeredPlayers);
    setLocalWaiting(waitingList);
    setHasChanges(false);
  };

  const allPlayers = [...localRegistered, ...localWaiting];

  return (
    <div ref={wrapperRef} className="space-y-4">
      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <svg
            className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">📱 Mobile Instructions:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Press and hold</strong> the drag icon (☰) for 300ms</li>
              <li><strong>Then drag</strong> to your desired position</li>
              <li>Move within same list or between main ↔ waiting</li>
              <li>Release to drop, then click &quot;Save Changes&quot;</li>
            </ul>
          </div>
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        autoScroll={{
          enabled: true,
          threshold: { x: 0.2, y: 0.2 },
          layoutShiftCompensation: false,
        }}
        modifiers={[restrictToVerticalAxis]}
      >
        {/* Registered Players */}
        <div>
          <h4 className="font-semibold text-gray-800 mb-2">
            Main List ({localRegistered.length}/{playerLimit})
          </h4>
          <SortableContext
            items={localRegistered.map((p) => p.user_id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {localRegistered.length > 0 ? (
                localRegistered.map((player, index) => (
                  <SortablePlayerItem
                    key={player.user_id}
                    player={player}
                    index={index}
                    listType="registered"
                  />
                ))
              ) : (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed">
                  No players in main list
                </div>
              )}
            </div>
          </SortableContext>
        </div>

        {/* Waiting List */}
        <div>
          <h4 className="font-semibold text-gray-800 mb-2">
            Waiting List ({localWaiting.length})
          </h4>
          <SortableContext
            items={localWaiting.map((p) => p.user_id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {localWaiting.length > 0 ? (
                localWaiting.map((player, index) => (
                  <SortablePlayerItem
                    key={player.user_id}
                    player={player}
                    index={index}
                    listType="waiting"
                  />
                ))
              ) : (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed">
                  No players in waiting list
                </div>
              )}
            </div>
          </SortableContext>
        </div>

        {/* Portal DragOverlay to document.body to escape modal coordinate system */}
        {createPortal(
          <DragOverlay dropAnimation={null} modifiers={[snapCenterToCursor]} style={{ zIndex: 10000 }}>
            {activeId ? (() => {
              const activePlayer = allPlayers.find((p) => p.user_id === activeId);
              const isInRegistered = localRegistered.find((p) => p.user_id === activeId);
              const listType = isInRegistered ? 'registered' : 'waiting';
              const index = isInRegistered 
                ? localRegistered.findIndex((p) => p.user_id === activeId)
                : localWaiting.findIndex((p) => p.user_id === activeId);
              
              return activePlayer ? (
                <div className={`flex items-center justify-between p-3 rounded-lg border shadow-2xl ${
                  listType === 'registered'
                    ? 'bg-green-50 border-green-300'
                    : 'bg-yellow-50 border-yellow-300'
                } ring-2 ring-blue-500 opacity-95 cursor-grabbing`}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="text-gray-500 font-mono text-sm flex-shrink-0">#{index + 1}</span>
                    <div className="flex items-center gap-2 min-w-0">
                      {activePlayer.profile_photo_url && (
                        <img
                          src={activePlayer.profile_photo_url}
                          alt={activePlayer.name}
                          className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                        />
                      )}
                      <span className="font-semibold text-gray-800 truncate">{activePlayer.name}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {activePlayer.paid && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                        Paid
                      </span>
                    )}
                    {activePlayer.is_first_game && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                        1st
                      </span>
                    )}
                    <svg
                      className="w-6 h-6 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M4 6h16M4 12h16M4 18h16"
                      />
                    </svg>
                  </div>
                </div>
              ) : null;
            })() : null}
          </DragOverlay>,
          document.body
        )}
      </DndContext>

      {/* Action Buttons */}
      {hasChanges && (
        <div className="flex gap-3 pt-4 border-t">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            onClick={handleReset}
            disabled={isSaving}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-colors disabled:opacity-50"
          >
            Reset
          </button>
        </div>
      )}
    </div>
  );
}
